import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskLine } from "./Reveal";
import { HERO_IMAGE, CONTACT } from "../../data";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-screen w-full overflow-hidden noise-overlay"
      data-testid="hero-section"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_IMAGE} alt="Industrial power facility" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-void" />
      <div className="absolute inset-0 bg-gradient-to-t from-void via-void/20 to-void/40" />

      <div className="relative z-10 mx-auto max-w-[1600px] px-5 md:px-10 pt-32 md:pt-40 pb-16 min-h-screen flex flex-col justify-between">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex items-center gap-4 font-mono text-xs uppercase tracking-[0.25em] text-forge"
        >
          <span className="w-10 h-px bg-forge" />
          Power & Cooling · Est. {CONTACT.since}
        </motion.div>

        <div>
          <h1 className="font-head font-black uppercase tracking-tighter leading-[0.82] text-6xl sm:text-7xl lg:text-[8.5rem]">
            <MaskLine delay={0.25}>Power that</MaskLine>
            <MaskLine delay={0.4} className="text-forge">never blinks.</MaskLine>
            <MaskLine delay={0.55}>Cooling that</MaskLine>
            <MaskLine delay={0.7} className="text-stroke">never quits.</MaskLine>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-10 flex flex-col md:flex-row md:items-end gap-8 md:justify-between"
          >
            <p className="max-w-md text-base md:text-lg text-plasma/70 leading-relaxed">
              Automatic & servo stabilizers, solar systems, inverters and industrial
              coolers — engineered in-house since 1986 for homes, businesses and heavy industry.
            </p>
            <div className="flex items-center gap-4">
              <a
                href="#products"
                data-testid="hero-explore-btn"
                className="font-mono text-xs uppercase tracking-[0.2em] bg-forge text-void px-7 py-4 hover:bg-electric transition-colors"
              >
                Explore Systems
              </a>
              <a
                href="#contact"
                data-testid="hero-quote-btn"
                className="font-mono text-xs uppercase tracking-[0.2em] border border-white/25 text-plasma px-7 py-4 hover:border-forge hover:text-forge transition-colors"
              >
                Enquire
              </a>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3 }}
          className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-plasma/50"
        >
          <ArrowDown size={14} className="animate-bounce text-forge" />
          Scroll to power up
        </motion.div>
      </div>
    </section>
  );
};
